import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, fmtEur } from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/ui-kit";
import { Plus, BookOpen, Search, Upload, X } from "lucide-react";
import { toast } from "sonner";

const emptyBook = {
  isbn: "", title: "", subtitle: "", authors: "", illustrators: "",
  pvp: 0, cost: 0, tax_rate: 4, pages: 0, age_range: "", cover_url: "",
  active: true, notes: "",
};

export default function Books() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBook);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  async function uploadCover(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, cover_url: data.url }));
      toast.success("Portada subida");
    } catch (e) {
      toast.error("Error subiendo imagen");
    } finally {
      setUploading(false);
    }
  }

  async function load() {
    const { data } = await api.get("/books", { params: q ? { q } : {} });
    setItems(data);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  function openNew() { setEditing(null); setForm(emptyBook); setShowForm(true); }
  function openEdit(b) { setEditing(b); setForm({ ...emptyBook, ...b }); setShowForm(true); }

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        pvp: parseFloat(form.pvp) || 0,
        cost: parseFloat(form.cost) || 0,
        tax_rate: parseFloat(form.tax_rate) || 4,
        pages: parseInt(form.pages) || 0,
      };
      if (editing) await api.put(`/books/${editing.id}`, payload);
      else await api.post("/books", payload);
      toast.success(editing ? "Libro actualizado" : "Libro creado");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  }

  async function remove(id) {
    if (!window.confirm("¿Eliminar este libro? (los movimientos históricos no se tocan)")) return;
    await api.delete(`/books/${id}`);
    toast.success("Libro eliminado");
    load();
  }

  return (
    <div data-testid="books-page">
      <PageHeader
        title="Catálogo"
        subtitle="Fichas de los títulos en activo. Busca por título, ISBN o autor/a."
        actions={
          <button onClick={openNew} data-testid="new-book-button"
            className="bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2 transition-colors">
            <Plus size={16} strokeWidth={1.5} /> Nuevo título
          </button>
        }
      />

      <div className="mb-5 max-w-md relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink2" />
        <input
          value={q} onChange={(e) => { setQ(e.target.value); setSp(e.target.value ? { q: e.target.value } : {}); }}
          placeholder="Filtrar catálogo…"
          data-testid="catalog-filter-input"
          className="w-full bg-white border border-line rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
        />
      </div>

      {items.length === 0 ? (
        <Section>
          <EmptyState
            title="Aún no hay títulos"
            description="Comienza creando las fichas de los libros de tu catálogo. Podrás añadir portadas, PVP, coste e ISBN."
            action={<button onClick={openNew} className="bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm">Crear primer título</button>}
            testid="books-empty"
          />
        </Section>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="books-grid">
          {items.map(b => (
            <div key={b.id} className="bg-surface border border-line rounded-sm p-4 flex gap-4 hover:-translate-y-[2px] hover:shadow-sm transition-all duration-300 group" data-testid={`book-card-${b.isbn}`}>
              <div className="w-20 h-28 shrink-0 bg-sidebar border border-line rounded-sm overflow-hidden flex items-center justify-center">
                {b.cover_url ? (
                  <img src={b.cover_url} alt={b.title} className="w-full h-full object-cover" />
                ) : (
                  <BookOpen size={22} strokeWidth={1.2} className="text-ink2" />
                )}
              </div>
              <div className="min-w-0 flex-1 flex flex-col">
                <div className="font-heading text-base text-ink leading-snug truncate">{b.title}</div>
                {b.subtitle && <div className="text-xs text-ink2 truncate">{b.subtitle}</div>}
                <div className="text-[11px] font-mono text-ink2 mt-1">{b.isbn}</div>
                <div className="text-xs text-ink2 mt-0.5 truncate">{b.authors || "—"}</div>
                <div className="mt-auto pt-3 flex items-center justify-between">
                  <span className="font-heading text-base text-brand font-num">{fmtEur(b.pvp)}</span>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(b)} data-testid={`edit-book-${b.isbn}`} className="text-xs text-ink2 hover:text-ink">Editar</button>
                    <button onClick={() => remove(b.id)} data-testid={`delete-book-${b.isbn}`} className="text-xs text-crimson hover:opacity-80">Borrar</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title={editing ? "Editar libro" : "Nuevo libro"}>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="book-form">
            <Field label="ISBN" required>
              <input value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                data-testid="book-isbn" required className={inputCls} />
            </Field>
            <Field label="Edad recomendada">
              <input value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })}
                className={inputCls} placeholder="p.ej. 3-6" />
            </Field>
            <Field label="Título" required span={2}>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="book-title" required className={inputCls} />
            </Field>
            <Field label="Subtítulo" span={2}>
              <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Autores/as">
              <input value={form.authors} onChange={(e) => setForm({ ...form, authors: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Ilustración">
              <input value={form.illustrators} onChange={(e) => setForm({ ...form, illustrators: e.target.value })} className={inputCls} />
            </Field>
            <Field label="PVP (EUR)">
              <input type="number" step="0.01" value={form.pvp} onChange={(e) => setForm({ ...form, pvp: e.target.value })}
                data-testid="book-pvp" className={inputCls} />
            </Field>
            <Field label="Coste unitario (EUR)">
              <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })}
                data-testid="book-cost" className={inputCls} />
            </Field>
            <Field label="IVA %">
              <input type="number" step="0.1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Páginas">
              <input type="number" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Portada" span={2}>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden" onChange={(e) => uploadCover(e.target.files[0])} />
              <div className="flex items-center gap-3">
                {form.cover_url ? (
                  <img src={form.cover_url} alt="portada" className="h-16 w-12 object-cover rounded-sm border border-line flex-shrink-0" />
                ) : (
                  <div className="h-16 w-12 bg-sidebar border border-line rounded-sm flex items-center justify-center flex-shrink-0">
                    <BookOpen size={18} className="text-ink2" />
                  </div>
                )}
                <div className="flex gap-2">
                  <button type="button" onClick={() => fileRef.current.click()} disabled={uploading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-line rounded-sm hover:bg-sidebar disabled:opacity-50">
                    <Upload size={13} /> {uploading ? "Subiendo…" : "Seleccionar imagen"}
                  </button>
                  {form.cover_url && (
                    <button type="button" onClick={() => setForm(f => ({ ...f, cover_url: "" }))}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-line rounded-sm hover:bg-sidebar text-ink2">
                      <X size={13} /> Quitar
                    </button>
                  )}
                </div>
              </div>
            </Field>
            <Field label="Notas" span={2}>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} />
            </Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">Cancelar</button>
              <button type="submit" data-testid="book-submit" className="px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const inputCls = "w-full bg-white border border-line rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";
function Field({ label, children, required, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.12em] text-ink2">{label}{required && <span className="text-brand"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export function Modal({ children, onClose, title, size = "md" }) {
  const sizes = { sm: "max-w-sm", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-[2px] p-4 animate-fade-up" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`w-full ${sizes[size]} bg-surface border border-line rounded-sm overflow-hidden max-h-[90vh] flex flex-col`} data-testid="modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h3 className="font-heading text-lg text-ink">{title}</h3>
          <button onClick={onClose} className="text-ink2 hover:text-ink text-xl leading-none" data-testid="modal-close">×</button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
